import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styles from "./ProcessActionEditor.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faSave,
  faPlus,
  faTrash,
  faTimes,
  faCube,
  faListCheck,
  faDrawPolygon,
} from "@fortawesome/free-solid-svg-icons";
import { ToastService } from "../../service/toast.service";
import { useLanguage } from "../../context/LanguageContext";
import { processApi, ProcessAction } from "../../helper/processApi";
import { postUser } from "../../helper/http";
import ShapeLibraryModal from "../../components/Transactions/ShapeLibraryModal/ShapeLibraryModal";
import ShapeGuideModal from "../../components/TransactionTemplate/Modals/ShapeGuideModal/ShapeGuideModal";
import SearchableDropdown from "../../components/SearchableDropdown/SearchableDropdown";

interface Transaction {
  id: number;
  name?: string;
}

interface ValidationField {
  id: string;
  fieldName: string;
  validators: string;
  description: string;
  isOptional: boolean;
}

type PrimitiveType =
  | "event"
  | "break"
  | "loop"
  | "return"
  | "function"
  | "throw"
  | "condition"
  | "primitive"
  | "subtransaction";

interface Primitive {
  id: string;
  name: string;
  x: number;
  y: number;
  type: PrimitiveType;
  data: any;
}

interface Connection {
  from: string;
  to: string;
  type: "primitive";
}

type TabType = "information" | "body";

export default function ProcessActionEditor() {
  const { processId, actionId } = useParams<{
    processId: string;
    actionId: string;
  }>();
  const navigate = useNavigate();
  const { t } = useLanguage();

  const isNewAction = actionId === "new";
  const numericProcessId = Number(processId);
  const numericActionId = isNewAction ? null : Number(actionId);

  const svgRef = useRef<SVGSVGElement>(null);

  const [activeTab, setActiveTab] = useState<TabType>("information");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [transactions, setTransactions] = useState<Transaction[]>([]);

  // Tab 1
  const [eventType, setEventType] = useState("");
  const [transactionId, setTransactionId] = useState<number | null>(null);
  const [validationFields, setValidationFields] = useState<ValidationField[]>(
    [],
  );

  // Tab 2
  const [primitives, setPrimitives] = useState<Primitive[]>([]);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [selectedPrimitive, setSelectedPrimitive] = useState<number | null>(
    null,
  );
  const [isEditMode, setIsEditMode] = useState(true);
  const [addMode, setAddMode] = useState<{
    afterPrimitive: number;
    branch?: "yes" | "no" | "after";
  } | null>(null);
  const [isShapeLibraryOpen, setIsShapeLibraryOpen] = useState(false);
  const [showShapeGuide, setShowShapeGuide] = useState(false);

  const PRIMITIVE_WIDTH = 120;
  const PRIMITIVE_HEIGHT = 80;

  const primaryColor = "#1f2937";
  const textColor = "#1f2937";

  const canvasContainerRef = useRef<HTMLDivElement>(null);

  const getThemeColor = () => {
    if (typeof window === "undefined") return "#2563eb";
    return (
      getComputedStyle(document.documentElement)
        .getPropertyValue("--prim")
        .trim() || "#2563eb"
    );
  };

  useEffect(() => {
    loadData();
  }, [processId, actionId]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (activeTab !== "body") return;

      const isArrow = [
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "ArrowDown",
      ].includes(e.key);

      if (
        selectedPrimitive === null &&
        e.altKey &&
        !e.ctrlKey &&
        isArrow &&
        isEditMode
      ) {
        e.preventDefault();
        if (primitives.length > 0) {
          setSelectedPrimitive(0);
        }
        return;
      }

      if (e.ctrlKey && (e.key === "e" || e.key === "у" || e.code === "KeyE")) {
        e.preventDefault();
        setIsEditMode((prev) => {
          const newMode = !prev;
          ToastService.info(newMode ? "Edit mode ON" : "Edit mode OFF");
          if (!newMode) {
            setSelectedPrimitive(null);
          }
          return newMode;
        });
        return;
      }

      if (!isEditMode) return;

      if (selectedPrimitive !== null && !e.ctrlKey) {
        const currentPrim = primitives[selectedPrimitive];
        if (!currentPrim) return;

        const Y_THRESHOLD = 60;

        if (e.code === "ArrowLeft") {
          e.preventDefault();
          const primitivesOnLeft = primitives
            .map((p, idx) => ({ prim: p, idx }))
            .filter(
              ({ prim }) =>
                prim.x < currentPrim.x &&
                Math.abs(prim.y - currentPrim.y) < Y_THRESHOLD,
            )
            .sort((a, b) => b.prim.x - a.prim.x);

          if (primitivesOnLeft.length > 0) {
            setSelectedPrimitive(primitivesOnLeft[0].idx);
          }
        } else if (e.code === "ArrowRight") {
          e.preventDefault();
          const primitivesOnRight = primitives
            .map((p, idx) => ({ prim: p, idx }))
            .filter(
              ({ prim }) =>
                prim.x > currentPrim.x &&
                Math.abs(prim.y - currentPrim.y) < Y_THRESHOLD,
            )
            .sort((a, b) => a.prim.x - b.prim.x);

          if (primitivesOnRight.length > 0) {
            setSelectedPrimitive(primitivesOnRight[0].idx);
          }
        } else if (e.code === "ArrowUp") {
          e.preventDefault();
          const primitivesAbove = primitives
            .map((p, idx) => ({ prim: p, idx }))
            .filter(({ prim }) => prim.y < currentPrim.y)
            .sort((a, b) => {
              const yDiff =
                Math.abs(a.prim.y - currentPrim.y) -
                Math.abs(b.prim.y - currentPrim.y);
              if (Math.abs(yDiff) > 1) return yDiff;
              return (
                Math.abs(a.prim.x - currentPrim.x) -
                Math.abs(b.prim.x - currentPrim.x)
              );
            });

          if (primitivesAbove.length > 0) {
            setSelectedPrimitive(primitivesAbove[0].idx);
          }
        } else if (e.code === "ArrowDown") {
          e.preventDefault();
          const primitivesBelow = primitives
            .map((p, idx) => ({ prim: p, idx }))
            .filter(({ prim }) => prim.y > currentPrim.y)
            .sort((a, b) => {
              const yDiff =
                Math.abs(a.prim.y - currentPrim.y) -
                Math.abs(b.prim.y - currentPrim.y);
              if (Math.abs(yDiff) > 1) return yDiff;
              return (
                Math.abs(a.prim.x - currentPrim.x) -
                Math.abs(b.prim.x - currentPrim.x)
              );
            });

          if (primitivesBelow.length > 0) {
            setSelectedPrimitive(primitivesBelow[0].idx);
          }
        }
        return;
      }

      if (selectedPrimitive !== null && e.ctrlKey) {
        const prim = primitives[selectedPrimitive];
        if (!prim) return;

        if (prim.type === "condition") {
          if (e.code === "ArrowUp") {
            e.preventDefault();
            setAddMode({ afterPrimitive: selectedPrimitive, branch: "no" });
            setIsShapeLibraryOpen(true);
          } else if (e.code === "ArrowDown") {
            e.preventDefault();
            setAddMode({ afterPrimitive: selectedPrimitive, branch: "yes" });
            setIsShapeLibraryOpen(true);
          } else if (e.code === "ArrowRight") {
            e.preventDefault();
            setAddMode({ afterPrimitive: selectedPrimitive, branch: "after" });
            setIsShapeLibraryOpen(true);
          }
        } else {
          if (e.code === "ArrowRight") {
            e.preventDefault();
            setAddMode({ afterPrimitive: selectedPrimitive, branch: "after" });
            setIsShapeLibraryOpen(true);
          }
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    selectedPrimitive,
    primitives,
    isEditMode,
    activeTab,
    isShapeLibraryOpen,
  ]);

  const loadData = async () => {
    setLoading(true);
    try {
      await loadTransactions();
      if (!isNewAction && numericActionId) {
        await loadAction(numericActionId);
      }
    } catch (e) {
      console.error("Error loading data:", e);
      ToastService.error(t("loadError") || "Load error");
    }
    setLoading(false);
  };

  const loadTransactions = async () => {
    try {
      const res = await postUser("crud/list", {
        module: "transaction",
        page: "1",
        per_page: "100",
        sort_direction: "+",
        sort_field: "id",
      });
      if (res.data) {
        setTransactions(res.data);
      }
    } catch (e) {
      console.error("Error loading transactions:", e);
    }
  };

  const loadAction = async (actionId: number) => {
    try {
      const actions = await processApi.getActions(numericProcessId);
      const action = actions.find((a) => a.id === actionId);

      if (action) {
        setEventType(action.eventType);
        setTransactionId(action.transactionId);

        if (Array.isArray(action.validations)) {
          setValidationFields(
            action.validations.map((v: any, idx) => ({
              id: `val-${idx}`,
              fieldName: v.fieldName || "",
              validators: Array.isArray(v.validators)
                ? v.validators.join(",")
                : v.validators || "",
              description: v.description || "",
              isOptional: !!v.isOptional,
            })),
          );
        }

        if (Array.isArray(action.fndBody)) {
          convertFndBodyToPrimitives(action.fndBody);
        }
      }
    } catch (e) {
      console.error("Error loading action:", e);
      ToastService.error(t("loadError") || "Load error");
    }
  };

  const convertFndBodyToPrimitives = (fndBody: any[]) => {
    const newPrimitives: Primitive[] = [];
    const newConnections: Connection[] = [];
    let primitiveCounter = 0;

    const processActions = (
      actions: any[],
      startX: number,
      startY: number,
      parentId?: string,
    ) => {
      let currentX = startX;
      let currentY = startY;
      let lastId: string | null = parentId || null;

      actions.forEach((action) => {
        const primId = `prim-${primitiveCounter++}`;
        let primName = "";

        switch (action.type) {
          case "function":
            primName = `${action.link ? action.link + " = " : ""}${action.func}`;
            break;
          case "if":
            primName = `if (${action.cond})`;
            break;
          case "throw":
            primName = `Error: ${action.funcParams?.[0] || "Unknown"}`;
            break;
          case "for":
            primName = `for ${action.id} in ${action.elems}`;
            break;
          case "subtransaction":
            primName = `Sub: ${action.transactionId}`;
            break;
          case "return":
            primName = `return ${action.func || "null"}`;
            break;
          case "break":
            primName = `break`;
            break;
          default:
            primName = action.func || action.type;
        }

        const primitive: Primitive = {
          id: primId,
          name: primName,
          x: currentX,
          y: currentY,
          type: action.type,
          data: { ...action },
        };

        newPrimitives.push(primitive);
        if (lastId)
          newConnections.push({ from: lastId, to: primId, type: "primitive" });
        lastId = primId;

        if (action.type === "if") {
          if (action.yesLine)
            processActions(
              action.yesLine,
              currentX + 220,
              currentY + 100,
              primId,
            );
          if (action.noLine)
            processActions(
              action.noLine,
              currentX + 220,
              currentY + 200,
              primId,
            );
          currentX += 440;
        } else if (action.type === "for" && action.bodyLine) {
          processActions(
            action.bodyLine,
            currentX + 220,
            currentY + 100,
            primId,
          );
          currentX += 220;
        } else {
          currentX += 220;
        }
      });
    };

    processActions(fndBody, 100, 100);
    setPrimitives(newPrimitives);
    setConnections(newConnections);
  };

  const convertPrimitivesToFndBody = (): any[] => {
    if (primitives.length === 0) return [];

    const processed = new Set<string>();
    const actions: any[] = [];

    const isValidAction = (action: any): boolean => {
      if (!action) return false;

      if (action.type === "primitive" || action.type === "return") {
        return !!(action.link?.trim() || action.func?.trim());
      }

      if (action.type === "if") {
        if (!action.cond?.trim()) return false;
        return true;
      }

      if (action.type === "for") {
        return !!(action.id?.trim() && action.elems?.trim());
      }

      return true;
    };

    const processChain = (primId: string): any | null => {
      if (processed.has(primId)) return null;
      const prim = primitives.find((p) => p.id === primId);
      if (!prim) return null;
      processed.add(primId);

      let action: any = { type: prim.type };

      switch (prim.type) {
        case "condition":
          action.cond = prim.data.cond || "true";
          action.yesLine = [];
          action.noLine = [];
          const ifConns = connections.filter((c) => c.from === primId);
          ifConns.forEach((conn) => {
            const target = primitives.find((p) => p.id === conn.to);
            if (target) {
              const yDiff = target.y - prim.y;
              if (Math.abs(yDiff - 100) < 30)
                action.yesLine = processBranch(conn.to, prim.y + 100);
              if (Math.abs(yDiff - 200) < 30)
                action.noLine = processBranch(conn.to, prim.y + 200);
            }
          });
          break;

        case "loop":
          action.id = prim.data.id || "item";
          action.elems = prim.data.elems || "";
          action.bodyLine = [];
          const bodyConn = connections.find((c) => c.from === primId);
          if (bodyConn) {
            const target = primitives.find((p) => p.id === bodyConn.to);
            if (target && target.y > prim.y)
              action.bodyLine = processBranch(bodyConn.to, prim.y + 100);
          }
          break;

        case "function":
          action.func = prim.data.func || "";
          action.funcParams = prim.data.funcParams || [];
          action.link = prim.data.link || "";
          break;

        case "throw":
          action.funcParams = [
            prim.data.errorMessage || "Error",
            prim.data.errorCode || "error_code",
          ];
          break;

        case "subtransaction":
          action.transactionId = prim.data.transactionId || 0;
          action.link = prim.data.link || "";
          break;

        case "return":
        case "primitive":
          action.func = prim.data.func || "null";
          action.link = prim.data.link || "";
          break;

        case "event":
          action.params = prim.data.params || {};
          break;
      }

      return action;
    };

    const processBranch = (startId: string, branchY: number): any[] => {
      const branch: any[] = [];

      let currentId: string | undefined = startId;

      while (currentId) {
        const prim = primitives.find((p) => p.id === currentId);
        if (!prim || Math.abs(prim.y - branchY) >= 30) break;

        if (!processed.has(currentId)) {
          const action = processChain(currentId);
          if (action) branch.push(action);
        }

        const nextConn = connections.find((c) => c.from === currentId);
        if (!nextConn) break;

        currentId = nextConn.to;
      }

      return branch;
    };

    const mainLinePrimitives = primitives
      .filter((p) => Math.abs(p.y - 100) < 30)
      .sort((a, b) => a.x - b.x);

    if (mainLinePrimitives.length > 0) {
      let startNode = mainLinePrimitives[0];

      let currentId: string | undefined = startNode.id;
      while (currentId) {
        const prim = primitives.find((p) => p.id === currentId);
        if (!prim || Math.abs(prim.y - 100) >= 30) break;

        if (!processed.has(currentId)) {
          const action = processChain(currentId);
          if (action) actions.push(action);
        }

        const nextConn = connections.find((c) => c.from === currentId);
        currentId = nextConn?.to;
      }
    }

    return actions;
  };

  const handleSave = async () => {
    try {
      if (!eventType.trim()) {
        ToastService.error(t("eventTypeRequired") || "Event type is required");
        setActiveTab("information");
        return;
      }

      if (!transactionId) {
        ToastService.error(
          t("transactionRequired") || "Transaction is required",
        );
        setActiveTab("information");
        return;
      }

      if (primitives.length === 0) {
        ToastService.error(
          t("bodyRequired") || "Body is required - add at least one element",
        );
        setActiveTab("body");
        return;
      }

      const incompleteElements = primitives.filter((prim) => {
        if (prim.type === "condition" && !prim.data.cond?.trim()) {
          return true;
        }
        if (
          prim.type === "loop" &&
          (!prim.data.id?.trim() || !prim.data.elems?.trim())
        ) {
          return true;
        }
        if (
          (prim.type === "primitive" || prim.type === "return") &&
          !prim.data.link?.trim() &&
          !prim.data.func?.trim()
        ) {
          return true;
        }
        return false;
      });

      if (incompleteElements.length > 0) {
        ToastService.error(
          t("incompleteElements") ||
            "Some elements are incomplete. Please fill in all required fields.",
        );
        setActiveTab("body");
        setSelectedPrimitive(primitives.indexOf(incompleteElements[0]));
        return;
      }

      const forLoopsWithoutBody = primitives.filter((prim) => {
        if (prim.type !== "loop") return false;
        const bodyY = prim.y + 100;
        const bodyElements = primitives.filter(
          (p) => Math.abs(p.y - bodyY) < 30 && p.x > prim.x,
        );
        return bodyElements.length === 0;
      });

      if (forLoopsWithoutBody.length > 0) {
        ToastService.error(
          t("forLoopNeedsBody") ||
            "FOR loop must have at least one element inside. Add elements below the loop.",
        );
        setActiveTab("body");
        return;
      }

      const ifWithoutBranches = primitives.filter((prim) => {
        if (prim.type !== "condition") return false;
        const yesY = prim.y + 100;
        const noY = prim.y + 200;

        const yesBranch = primitives.filter(
          (p) => Math.abs(p.y - yesY) < 30 && p.x > prim.x,
        );
        const noBranch = primitives.filter(
          (p) => Math.abs(p.y - noY) < 30 && p.x > prim.x,
        );

        return yesBranch.length === 0 && noBranch.length === 0;
      });

      if (ifWithoutBranches.length > 0) {
        ToastService.error(
          t("ifNeedsBranches") ||
            "IF condition must have at least one branch (YES or NO).",
        );
        setActiveTab("body");
        return;
      }

      setSaving(true);

      const fndBody = convertPrimitivesToFndBody();

      if (fndBody.length === 0) {
        ToastService.error(
          t("bodyRequired") ||
            "Body schema must contain at least one valid element chain",
        );
        setActiveTab("body");
        setSaving(false);
        return;
      }

      const actionData: any = {
        processId: numericProcessId,
        eventType: eventType.trim(),
        transactionId,
        validations: validationFields
          .filter((v) => v.fieldName.trim() !== "")
          .map((v) => ({
            fieldName: v.fieldName.trim(),
            validators: v.validators
              ? v.validators
                  .split(",")
                  .map((s) => s.trim())
                  .filter((s) => s.length > 0)
              : [],
            description: v.description.trim(),
            isOptional: !!v.isOptional,
          })),
        fndBody: fndBody,
      };

      if (!isNewAction && numericActionId) {
        actionData.id = numericActionId;
      }

      await postUser("crud/upsert", {
        body: actionData,
        module: "process-action",
      });

      ToastService.success(
        isNewAction
          ? t("actionCreated") || "Action created successfully"
          : t("actionUpdated") || "Action updated successfully",
      );

      navigate(`/process-action/${processId}`);
    } catch (e: any) {
      console.error("Error saving action:", e);
      console.error("Response data:", e.response?.data);

      const errorMessage =
        e.response?.data?.message || e.response?.data?.error || e.message;
      ToastService.error(
        t("saveError") || `Failed to save action: ${errorMessage}`,
      );
    } finally {
      setSaving(false);
    }
  };

  const handleAddValidationField = () => {
    setValidationFields([
      ...validationFields,
      {
        id: `val-${Date.now()}`,
        fieldName: "",
        validators: "",
        description: "",
        isOptional: false,
      },
    ]);
  };

  const handleUpdateValidationField = (
    id: string,
    field: keyof ValidationField,
    value: any,
  ) => {
    setValidationFields(
      validationFields.map((v) => (v.id === id ? { ...v, [field]: value } : v)),
    );
  };

  const handleRemoveValidationField = (id: string) => {
    setValidationFields(validationFields.filter((v) => v.id !== id));
  };

  const handleSelectShape = (shapeType: string, shapeName: string) => {
    const newPrim = {
      id: `prim-${Date.now()}`,
      name: shapeName,
      x: 100,
      y: 100,
      type: shapeType as any,
      data: { _isNew: true, type: shapeType },
    };
    setPrimitives([...primitives, newPrim]);
    setIsShapeLibraryOpen(false);
  };

  const handleUpdatePrimitive = (id: string, data: any) => {
    setPrimitives(
      primitives.map((p) =>
        p.id === id
          ? {
              ...p,
              name: data.cond || data.id || data.link || data.func || p.name,
              data,
            }
          : p,
      ),
    );
  };

  const handleRemovePrimitive = (id: string) => {
    setPrimitives(primitives.filter((p) => p.id !== id));
    setConnections(connections.filter((c) => c.from !== id && c.to !== id));
    setSelectedPrimitive(null);
  };

  const calculateSchemaDimensions = () => {
    return { width: 2000, height: 1000 };
  };

  const renderPrimitive = (prim: Primitive, index: number) => {
    const isSelected = selectedPrimitive === index;
    const themeColor = getThemeColor();
    const strokeColor = isSelected ? themeColor : "#000000";
    const strokeWidth = isSelected ? 3 : 2;
    const fillColor = "#ffffff";

    const baseX = prim.x;
    const baseY = prim.y;
    const width = prim.type === "subtransaction" ? 140 : PRIMITIVE_WIDTH;
    const height = PRIMITIVE_HEIGHT;

    let shape;
    switch (prim.type) {
      case "condition":
        shape = (
          <polygon
            points={`${baseX + width / 2},${baseY} ${baseX + width},${baseY + height / 2} ${baseX + width / 2},${baseY + height} ${baseX},${baseY + height / 2}`}
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth={strokeWidth}
          />
        );
        break;
      case "loop":
        const offset = 15;
        shape = (
          <polygon
            points={`
            ${baseX + offset},${baseY} 
            ${baseX + width - offset},${baseY} 
            ${baseX + width},${baseY + height / 2} 
            ${baseX + width - offset},${baseY + height} 
            ${baseX + offset},${baseY + height} 
            ${baseX},${baseY + height / 2}
          `}
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth={strokeWidth}
          />
        );
        break;
      case "return":
      case "break":
        shape = (
          <rect
            x={baseX}
            y={baseY}
            width={width}
            height={height}
            rx={height / 2}
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth={strokeWidth}
          />
        );
        break;
      case "throw":
        shape = (
          <rect
            x={baseX}
            y={baseY}
            width={width}
            height={height}
            rx="4"
            fill={fillColor}
            stroke={isSelected ? themeColor : "#ef4444"}
            strokeWidth={strokeWidth}
            strokeDasharray="5,3"
          />
        );
        break;
      default:
        shape = (
          <rect
            x={baseX}
            y={baseY}
            width={width}
            height={height}
            rx="4"
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth={strokeWidth}
          />
        );
    }

    return (
      <g
        key={prim.id}
        onClick={(e) => {
          e.stopPropagation();
          setSelectedPrimitive(index);
        }}
        style={{ cursor: "pointer" }}
      >
        {shape}
        <foreignObject
          x={baseX + 5}
          y={baseY + 5}
          width={width - 10}
          height={height - 10}
        >
          <div
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              fontSize: "11px",
              fontWeight: "600",
              color: "#000000",
              wordBreak: "break-word",
              pointerEvents: "none",
            }}
          >
            {prim.name}
          </div>
        </foreignObject>
      </g>
    );
  };

  const renderConnections = () => {
    return connections.map((conn, idx) => {
      const fromPrim = primitives.find((p) => p.id === conn.from);
      const toPrim = primitives.find((p) => p.id === conn.to);
      if (!fromPrim || !toPrim) return null;

      const fromX =
        fromPrim.x +
        (fromPrim.type === "subtransaction" ? 140 : PRIMITIVE_WIDTH);
      const fromY = fromPrim.y + PRIMITIVE_HEIGHT / 2;

      const toX = toPrim.x;
      const toY = toPrim.y + PRIMITIVE_HEIGHT / 2;

      const sameRow = Math.abs(fromY - toY) < 20;
      let pathD = "";

      if (sameRow) {
        pathD = `M ${fromX} ${fromY} L ${toX} ${toY}`;
      } else {
        const midX = fromX + (toX - fromX) / 2;
        const radius = 12;
        const direction = toY > fromY ? 1 : -1;

        pathD = `
        M ${fromX} ${fromY}
        L ${midX - radius} ${fromY}
        Q ${midX} ${fromY} ${midX} ${fromY + radius * direction}
        L ${midX} ${toY - radius * direction}
        Q ${midX} ${toY} ${midX + radius} ${toY}
        L ${toX} ${toY}
      `
          .replace(/\s+/g, " ")
          .trim();
      }

      return (
        <path
          key={`conn-${idx}`}
          d={pathD}
          fill="none"
          stroke="#000000"
          strokeWidth="2"
          markerEnd="url(#arrowhead)"
          style={{ pointerEvents: "none" }}
        />
      );
    });
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner}></div>
        <p>{t("loading") || "Loading..."}</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <button
            className={styles.backButton}
            onClick={() => navigate(`/process-action/${processId}`)}
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          <div className={styles.titleWrapper}>
            <h1>
              {isNewAction
                ? t("createAction") || "New Action"
                : t("editAction") || "Edit Action"}
            </h1>
            <span className={styles.subtitle}>
              {isNewAction
                ? "Configure a new trigger"
                : `Editing action #${actionId}`}
            </span>
          </div>
        </div>

        <div className={styles.headerActions}>
          <button
            className={styles.saveButton}
            onClick={handleSave}
            disabled={saving}
          >
            <FontAwesomeIcon icon={faSave} />
            {saving ? t("saving") || "Saving..." : t("save") || "Save Changes"}
          </button>
        </div>
      </div>

      <div className={styles.tabsContainer}>
        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${activeTab === "information" ? styles.tabActive : ""}`}
            onClick={() => setActiveTab("information")}
          >
            <FontAwesomeIcon icon={faListCheck} />
            {t("basicInformation") || "Config & Validation"}
          </button>
          <button
            className={`${styles.tab} ${activeTab === "body" ? styles.tabActive : ""}`}
            onClick={() => {
              setActiveTab("body");
              setTimeout(() => canvasContainerRef.current?.focus(), 0);
            }}
          >
            <FontAwesomeIcon icon={faDrawPolygon} />
            {t("body") || "Pipeline Builder"}
          </button>
        </div>
      </div>

      <div className={styles.tabContent}>
        {activeTab === "information" && (
          <div className={styles.informationTab}>
            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <FontAwesomeIcon icon={faCube} className={styles.cardIcon} />
                <h3>Configuration</h3>
              </div>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    {t("eventType") || "Event Type"}{" "}
                    <span className={styles.required}>*</span>
                  </label>
                  <input
                    type="text"
                    className={styles.input}
                    placeholder="e.g. on_payment_success"
                    value={eventType}
                    onChange={(e) => setEventType(e.target.value)}
                  />
                </div>

                <div className={styles.formGroup}>
                  <SearchableDropdown
                    label={t("transactionModule") || "Target Transaction *"}
                    options={transactions.map((trans) => ({
                      label: trans.name || `Transaction ${trans.id}`,
                      value: String(trans.id),
                    }))}
                    value={transactionId ? String(transactionId) : ""}
                    onChange={(value) =>
                      setTransactionId(value ? Number(value) : null)
                    }
                  />
                </div>
              </div>
            </div>

            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <div className={styles.headerTitle}>
                  <FontAwesomeIcon
                    icon={faListCheck}
                    className={styles.cardIcon}
                  />
                  <h3>{t("validationFields") || "Input Validations"}</h3>
                </div>
                <button
                  className={styles.addButtonSecondary}
                  onClick={handleAddValidationField}
                >
                  <FontAwesomeIcon icon={faPlus} />
                  {t("addField") || "Add Field"}
                </button>
              </div>

              <div className={styles.validationList}>
                {validationFields.length === 0 ? (
                  <div className={styles.emptyState}>
                    <p>
                      {t("noValidationFields") ||
                        "No validation fields defined."}
                    </p>
                    <small>
                      Add fields to validate incoming data before processing.
                    </small>
                  </div>
                ) : (
                  <div className={styles.tableWrapper}>
                    <div className={styles.tableHeader}>
                      <div className={styles.colName}>Field Name</div>
                      <div className={styles.colValidators}>Validators</div>
                      <div className={styles.colOptional}>Optional</div>
                      <div className={styles.colDesc}>Description</div>
                      <div className={styles.colAction}></div>
                    </div>

                    {validationFields.map((field) => (
                      <div key={field.id} className={styles.tableRow}>
                        <div className={styles.colName}>
                          <input
                            type="text"
                            className={styles.tableInput}
                            placeholder="field_name"
                            value={field.fieldName}
                            onChange={(e) =>
                              handleUpdateValidationField(
                                field.id,
                                "fieldName",
                                e.target.value,
                              )
                            }
                          />
                        </div>

                        <div className={styles.colValidators}>
                          <input
                            type="text"
                            className={styles.tableInput}
                            placeholder="required, string"
                            value={field.validators}
                            onChange={(e) =>
                              handleUpdateValidationField(
                                field.id,
                                "validators",
                                e.target.value,
                              )
                            }
                          />
                        </div>

                        <div className={styles.colOptional}>
                          <label className={styles.checkboxContainer}>
                            <input
                              type="checkbox"
                              className={styles.hiddenCheckbox}
                              checked={field.isOptional}
                              onChange={(e) =>
                                handleUpdateValidationField(
                                  field.id,
                                  "isOptional",
                                  e.target.checked,
                                )
                              }
                            />
                            <span className={styles.customCheckbox} />
                          </label>
                        </div>

                        <div className={styles.colDesc}>
                          <input
                            type="text"
                            className={styles.tableInput}
                            placeholder="Description..."
                            value={field.description}
                            onChange={(e) =>
                              handleUpdateValidationField(
                                field.id,
                                "description",
                                e.target.value,
                              )
                            }
                          />
                        </div>

                        <div className={styles.colAction}>
                          <button
                            className={styles.rowActionBtn}
                            onClick={() =>
                              handleRemoveValidationField(field.id)
                            }
                            title="Remove"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === "body" && (
          <div className={styles.bodyTab}>
            <div
              ref={canvasContainerRef}
              className={styles.canvasContainer}
              tabIndex={-1}
              style={{ outline: "none" }}
            >
              <svg
                ref={svgRef}
                className={styles.svg}
                width={calculateSchemaDimensions().width}
                height={calculateSchemaDimensions().height}
                onClick={() => setSelectedPrimitive(null)}
              >
                <defs>
                  <pattern
                    id="grid"
                    width="25"
                    height="25"
                    patternUnits="userSpaceOnUse"
                  >
                    <circle cx="2" cy="2" r="1" fill="#f3f4f6" />
                  </pattern>
                  <marker
                    id="arrowhead"
                    markerWidth="10"
                    markerHeight="7"
                    refX="9"
                    refY="3.5"
                    orient="auto"
                  >
                    <polygon points="0 0, 10 3.5, 0 7" fill="#000000" />
                  </marker>
                </defs>
                <rect width="100%" height="100%" fill="url(#grid)" />

                {renderConnections()}
                {primitives.map((prim, idx) => (
                  <g key={prim.id}>{renderPrimitive(prim, idx)}</g>
                ))}
              </svg>

              <div className={styles.canvasToolbar}>
                <button
                  className={styles.toolbarBtn}
                  onClick={() => setIsShapeLibraryOpen(true)}
                  title={t("addShape")}
                >
                  <FontAwesomeIcon icon={faPlus} />
                  <span>Add Node</span>
                </button>
              </div>

              {selectedPrimitive !== null && primitives[selectedPrimitive] && (
                <div className={styles.propertyPanel}>
                  <div className={styles.panelHeader}>
                    <h4>Properties</h4>
                    <button
                      className={styles.closePanelBtn}
                      onClick={() => setSelectedPrimitive(null)}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>

                  <div className={styles.panelBody}>
                    <div className={styles.panelRow}>
                      <span className={styles.panelLabel}>Type</span>
                      <span className={styles.panelValueBadge}>
                        {primitives[selectedPrimitive].type}
                      </span>
                    </div>

                    {primitives[selectedPrimitive].type === "condition" && (
                      <div className={styles.fieldGroup}>
                        <label>Condition</label>
                        <input
                          type="text"
                          className={styles.input}
                          placeholder="x > 10"
                          value={primitives[selectedPrimitive].data.cond || ""}
                          onChange={(e) =>
                            handleUpdatePrimitive(
                              primitives[selectedPrimitive].id,
                              {
                                ...primitives[selectedPrimitive].data,
                                cond: e.target.value,
                              },
                            )
                          }
                        />
                      </div>
                    )}

                    {(primitives[selectedPrimitive].type === "primitive" ||
                      primitives[selectedPrimitive].type === "return") && (
                      <>
                        <div className={styles.fieldGroup}>
                          <label>
                            {primitives[selectedPrimitive].type === "return"
                              ? "Return Value"
                              : "Variable Name"}
                          </label>
                          <input
                            type="text"
                            className={styles.input}
                            placeholder="result"
                            value={
                              primitives[selectedPrimitive].data.link || ""
                            }
                            onChange={(e) =>
                              handleUpdatePrimitive(
                                primitives[selectedPrimitive].id,
                                {
                                  ...primitives[selectedPrimitive].data,
                                  link: e.target.value,
                                },
                              )
                            }
                          />
                        </div>
                        <div className={styles.fieldGroup}>
                          <label>Function / Expression</label>
                          <input
                            type="text"
                            className={styles.input}
                            placeholder="calculate()"
                            value={
                              primitives[selectedPrimitive].data.func || ""
                            }
                            onChange={(e) =>
                              handleUpdatePrimitive(
                                primitives[selectedPrimitive].id,
                                {
                                  ...primitives[selectedPrimitive].data,
                                  func: e.target.value,
                                },
                              )
                            }
                          />
                        </div>
                      </>
                    )}

                    <div className={styles.panelActions}>
                      <button
                        className={styles.deleteButton}
                        onClick={() =>
                          handleRemovePrimitive(
                            primitives[selectedPrimitive].id,
                          )
                        }
                      >
                        <FontAwesomeIcon icon={faTrash} />
                        Delete Node
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <ShapeLibraryModal
        isOpen={isShapeLibraryOpen}
        onClose={() => {
          setIsShapeLibraryOpen(false);
          setAddMode(null);
        }}
        onSelectShape={handleSelectShape}
      />
    </div>
  );
}
